/**
 * Loader - WebGPU Texture Preloader
 *
 * Loads images and uploads them as GPUTextures using kido/gpu.
 * Progress tracks actual GPU uploads, not just image decodes.
 *
 * Optimizations:
 * - Current route textures load first (prioritized)
 * - Uses fetch + createImageBitmap for faster decode (no <img> DOM overhead)
 * - Concurrent uploads capped to avoid GPU queue stalls
 * - ImageBitmaps freed after GPU upload (in createTextureFromImage)
 * - onComplete fires when current route is ready (deferred routes load in background)
 *
 * Video textures (type: "video") are skipped here -- the media slot
 * is still created so the scene knows a plane should exist, but the
 * actual VideoTexture is created by the scene from the DOM <video> element.
 */

import { createSampler, createTextureFromImage } from "kido/gpu";

import {
  App,
  type RouteMediaSlot,
  type RouteTextureConfig,
  type GpuInstance,
} from "@app/context";

export type LoaderConfig = {
  onProgress?: ((loaded: number, total: number) => void) | undefined;
  onComplete?: (() => void) | undefined;
  /** Pre-fetched bitmaps from Loader.prefetchCurrentRoute() */
  prefetched?: Map<string, Promise<ImageBitmap | null>> | undefined;
};

/** Max concurrent fetch+decode+upload tasks */
const MAX_CONCURRENT = 6;

interface TextureTask {
  url: string;
  routeUrl: string;
  element: string;
  index: number;
  layer: number;
  mediaSlot: RouteMediaSlot;
}

export default class Loader {
  private onComplete: (() => void) | null;
  private onProgress: ((loaded: number, total: number) => void) | null;
  private loadedCount: number;
  private totalCount: number;
  private loading: boolean;
  private startTime: number;
  private prefetched: Map<string, Promise<ImageBitmap | null>>;

  /**
   * Returns the shared parent scene key for routes that share a scene.
   * E.g., "/projects/slug" → "/projects", "/play/slug" → "/play".
   */
  private static _sharedSceneKey(route: string): string | null {
    const pageKey = App.config.routes[route];
    if (pageKey === "project") return "/projects";
    if (pageKey === "play-detail") return "/play";
    return null;
  }

  /**
   * Start fetching + decoding current-route images before GPU device exists.
   * Call this in parallel with gpu.load() to overlap network I/O with GPU init.
   * Pass the returned map as `prefetched` to the Loader constructor.
   */
  static prefetchCurrentRoute(): Map<string, Promise<ImageBitmap | null>> {
    const cache = new Map<string, Promise<ImageBitmap | null>>();
    const textureList = App.data.gpu.textures;
    const currentRoute = App.route.new.url ?? "/";
    const sharedScene = Loader._sharedSceneKey(currentRoute);

    for (const routeUrl of Object.keys(textureList)) {
      // Prefetch current route, shared "/" scene, parent shared scene,
      // and /projects + /play (always needed for navigation)
      if (
        routeUrl !== currentRoute &&
        routeUrl !== "/" &&
        routeUrl !== sharedScene &&
        routeUrl !== "/projects" &&
        routeUrl !== "/play"
      ) continue;

      const config = textureList[routeUrl] as RouteTextureConfig | undefined;
      const store = config?.textures;
      if (!store) continue;

      for (const element of Object.keys(store)) {
        for (const layers of store[element] ?? []) {
          if (!layers) continue;
          for (const layer of layers) {
            if (!layer?.url || layer.type === "video") continue;
            if (cache.has(layer.url)) continue;

            // Start fetch + decode now (no GPU needed yet)
            const imgUrl = layer.url;
            cache.set(
              imgUrl,
              fetch(imgUrl)
                .then((r) => (r.ok ? r.blob() : null))
                .then((b) => (b ? createImageBitmap(b) : null))
                .catch(() => {
                  // Retry without fm=webp if decode fails
                  if (imgUrl.includes("fm=webp")) {
                    const fallback = imgUrl.replace(/&fm=webp/, "");
                    return fetch(fallback)
                      .then((r) => (r.ok ? r.blob() : null))
                      .then((b) => (b ? createImageBitmap(b) : null))
                      .catch(() => null);
                  }
                  return null;
                })
            );
          }
        }
      }
    }

    console.debug("[loader:prefetch]", cache.size, "images started");
    return cache;
  }

  constructor(config?: LoaderConfig) {
    this.onComplete = config?.onComplete ?? null;
    this.onProgress = config?.onProgress ?? null;
    this.loadedCount = 0;
    this.totalCount = 0;
    this.loading = false;
    this.startTime = 0;
    this.prefetched = config?.prefetched ?? new Map();

    this.preload();
  }

  preload(): void {
    this.loading = true;
    this.startTime = performance.now();

    const gpu: GpuInstance | null = App.gpu as GpuInstance | null;
    if (!gpu) {
      this.finish();
      return;
    }

    gpu.media = {};

    const textureList = App.data.gpu.textures;
    const routeUrls = Object.keys(textureList);
    const currentRoute = App.route.new.url ?? "/";
    const sharedScene = Loader._sharedSceneKey(currentRoute);

    // Build task lists: current route first, then deferred routes
    const currentTasks: TextureTask[] = [];
    const deferredTasks: TextureTask[] = [];

    for (const routeUrl of routeUrls) {
      const config = textureList[routeUrl] as RouteTextureConfig | undefined;
      const store = config?.textures;
      if (!store) continue;

      gpu.media[routeUrl] = {};

      for (const element of Object.keys(store)) {
        const indices = store[element] ?? [];
        gpu.media[routeUrl][element] = [];

        for (let index = 0; index < indices.length; index++) {
          const layers = indices[index];
          if (!layers) continue;

          const isVideo = layers.some((l) => l?.type === "video");

          const mediaSlot: RouteMediaSlot = {
            attrib: [],
            sampler: null,
            data: [],
            layerL: layers.length,
            wh: 1,
            id: layers[0]?.id,
            type: isVideo ? "video" : "image",
          };

          gpu.media[routeUrl][element][index] = mediaSlot;

          if (isVideo) continue;

          for (let layer = 0; layer < layers.length; layer++) {
            const src = layers[layer];
            if (!src?.url) continue;

            const task: TextureTask = {
              url: src.url,
              routeUrl,
              element,
              index,
              layer,
              mediaSlot,
            };

            // Prioritize: current route, shared "/" scene, parent shared scene,
            // and /projects + /play (always needed for navigation)
            if (
              routeUrl === currentRoute ||
              routeUrl === "/" ||
              routeUrl === sharedScene ||
              routeUrl === "/projects" ||
              routeUrl === "/play"
            ) {
              currentTasks.push(task);
            } else {
              deferredTasks.push(task);
            }
          }
        }
      }
    }

    this.totalCount = currentTasks.length;

    console.debug(
      "[loader:start]",
      this.totalCount,
      "textures",
      `(${currentTasks.length} priority,`,
      `${deferredTasks.length} deferred)`
    );

    this.reportProgress();

    if (this.totalCount === 0) {
      this.finish();
      return;
    }

    // Load current route first, then deferred
    this.runQueue(currentTasks, gpu).then(() => {
      // Current route ready — fire onComplete so the app can start rendering
      const elapsed = Math.round(performance.now() - this.startTime);
      console.debug("[loader:critical-ready]", elapsed + "ms");
      this.finish();

      // Continue loading deferred routes in background
      if (deferredTasks.length > 0) {
        this.runQueue(deferredTasks, gpu).then(() => {
          const total = Math.round(performance.now() - this.startTime);
          console.debug("[loader:all-done]", total + "ms");
        });
      }
    });
  }

  /**
   * Process a queue of texture tasks with bounded concurrency.
   */
  private async runQueue(
    tasks: TextureTask[],
    gpu: GpuInstance
  ): Promise<void> {
    const device = gpu.device;
    if (!device) return;

    let cursor = 0;

    const next = async (): Promise<void> => {
      while (cursor < tasks.length) {
        const task = tasks[cursor++];
        if (!task) continue;
        await this.loadOne(task, device);
      }
    };

    // Launch up to MAX_CONCURRENT workers
    const workers: Promise<void>[] = [];
    for (let i = 0; i < Math.min(MAX_CONCURRENT, tasks.length); i++) {
      workers.push(next());
    }

    await Promise.all(workers);
  }

  /**
   * Fetch, decode, and upload a single texture.
   * Uses prefetched bitmap if available (already decoded during GPU init).
   */
  private async fetchBitmap(url: string): Promise<ImageBitmap | null> {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    try {
      const bm = await createImageBitmap(blob);
      if (bm.width === 0 || bm.height === 0) {
        bm.close();
        return null;
      }
      return bm;
    } catch {
      // WebP conversion from CDN may return corrupt data — retry without fm=webp
      if (url.includes("fm=webp")) {
        const fallbackUrl = url.replace(/&fm=webp/, "");
        const r2 = await fetch(fallbackUrl);
        if (!r2.ok) return null;
        const b2 = await r2.blob();
        const bm2 = await createImageBitmap(b2);
        if (bm2.width === 0 || bm2.height === 0) {
          bm2.close();
          return null;
        }
        return bm2;
      }
      return null;
    }
  }

  private async loadOne(
    task: TextureTask,
    device: GPUDevice
  ): Promise<void> {
    try {
      let bitmap: ImageBitmap | null = null;

      // Check for prefetched bitmap (decoded in parallel with GPU init)
      const prefetchPromise = this.prefetched.get(task.url);
      if (prefetchPromise) {
        bitmap = await prefetchPromise;
        this.prefetched.delete(task.url);
        if (bitmap && (bitmap.width === 0 || bitmap.height === 0)) {
          bitmap.close();
          bitmap = null;
        }
      }

      // Fall back to fetch + decode if no prefetch or prefetch failed
      if (!bitmap) {
        bitmap = await this.fetchBitmap(task.url);
      }

      // Retry once — proxy rewrites can occasionally return incomplete responses
      if (!bitmap) {
        bitmap = await this.fetchBitmap(task.url);
      }

      if (!bitmap) {
        console.warn("[loader] failed to decode image:", task.url);
        task.mediaSlot.attrib[task.layer] = null;
        task.mediaSlot.data[task.layer] = { v: false, element: null };
        this.onLoaded();
        return;
      }

      // Save dimensions before GPU upload (createTextureFromImage closes the bitmap)
      const aspectRatio = bitmap.width / bitmap.height;
      const tex = await createTextureFromImage(device, bitmap);
      task.mediaSlot.attrib[task.layer] = tex;
      task.mediaSlot.data[task.layer] = { v: false, element: null };
      task.mediaSlot.wh = aspectRatio;

      if (!task.mediaSlot.sampler) {
        task.mediaSlot.sampler = createSampler(device);
      }
    } catch (e) {
      console.warn("[loader] failed:", task.url, e);
      task.mediaSlot.attrib[task.layer] = null;
      task.mediaSlot.data[task.layer] = { v: false, element: null };
    }

    this.onLoaded();
  }

  private onLoaded(): void {
    this.loadedCount++;
    this.reportProgress();
  }

  private reportProgress(): void {
    this.onProgress?.(this.loadedCount, this.totalCount);
  }

  private finish(): void {
    this.loading = false;
    const elapsed = Math.round(performance.now() - this.startTime);
    console.debug("[loader:done]", elapsed + "ms");

    const gpu = App.gpu as GpuInstance | null;
    if (gpu?.renderer) {
      gpu.renderer.needsRedraw = true;
      gpu.renderer.extraFrames = 2;
    }

    this.onComplete?.();
    this.onComplete = null;

    // Sync textures into planes that were created before all textures loaded
    if (gpu?.scene?.syncTexturesFromSlots?.()) {
      if (gpu.renderer) {
        gpu.renderer.needsRedraw = true;
        gpu.renderer.extraFrames = 2;
      }
    }
  }

  /**
   * Clean up all loaded textures.
   * Call this when unloading the page or switching contexts.
   */
  unload(): void {
    // Clean up WebGPU textures
    const gpu = App.gpu as GpuInstance | null;
    if (gpu?.device && gpu.media) {
      for (const url of Object.keys(gpu.media)) {
        const routeMedia = gpu.media[url];
        if (!routeMedia) continue;
        for (const element of Object.keys(routeMedia)) {
          const slots = routeMedia[element];
          if (!slots) continue;
          for (const slot of slots) {
            if (!slot?.attrib) continue;
            for (const tex of slot.attrib) {
              if (tex) tex.destroy();
            }
            slot.attrib = [];
            slot.data = [];
            slot.sampler = null;
          }
        }
      }
      gpu.media = {};
    }

    this.onComplete = null;
    this.onProgress = null;
  }
}
